import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

type Voice = {name: string; gender?: string; description?: string}

type Props = {
  voices: Record<string, Voice[]> | null
  initialSelectedVoice?: string | null
  onChoice: (voiceName: string | null) => void
  onCancel?: () => void
}

const VoiceSelectionModal: React.FC<Props> = ({
  voices,
  initialSelectedVoice,
  onChoice,
  onCancel,
}) => {
  const intl = useIntl()
  const [selected, setSelected] = useState<string | null>(initialSelectedVoice ?? null)

  useEffect(() => {
    setSelected(initialSelectedVoice ?? null)
  }, [initialSelectedVoice])

  const flatVoices = useMemo(() => {
    if (!voices)
      return [] as Array<{name: string; gender?: string; description?: string; locale?: string}>
    const list: Array<{name: string; gender?: string; description?: string; locale?: string}> = []
    for (const l of Object.keys(voices)) {
      const arr = voices[l] || []
      for (const v of arr) {
        list.push({...v, locale: l})
      }
    }
    return list
  }, [voices])

  useEffect(() => {
    if (initialSelectedVoice == null && flatVoices.length > 0) {
      setSelected((prev) => prev ?? flatVoices[0].name)
    }
  }, [initialSelectedVoice, flatVoices])

  const title = intl.formatMessage({id: 'CHAT.VOICE.SELECT_VOICE'})
  const confirmLabel = intl.formatMessage({id: 'GENERAL.OK'})
  const cancelLabel = intl.formatMessage({id: 'GENERAL.CANCEL'})

  const handleConfirm = useCallback(() => {
    onChoice(selected || null)
  }, [onChoice, selected])

  const handleCancel = useCallback(() => {
    if (onCancel) onCancel()
    else onChoice(null)
  }, [onCancel, onChoice])

  const formatVoiceLabel = (name?: string) => {
    if (!name) return ''
    const parts = name.split('-')
    const last = parts.length > 0 ? parts[parts.length - 1] : name
    const cleaned = last.replace(/[_.]/g, ' ')
    const spaced = cleaned.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    return spaced.trim()
  }

  return (
    <div className='aichoice-backdrop' role='dialog' aria-modal='true' aria-label={title}>
      <div className='aichoice-modal'>
        <div className='aichoice-title'>{title}</div>
        <div style={{padding: '12px 16px'}}>
          <div style={{marginBottom: 8}}>
            <label style={{display: 'block', marginBottom: 6}}>
              {intl.formatMessage({id: 'CHAT.VOICE.VOICE'})}
            </label>
            <select
              value={selected || ''}
              onChange={(e) => setSelected(e.target.value)}
              style={{width: '100%', padding: '8px'}}
            >
              {flatVoices.map((v) => (
                <option key={v.name} value={v.name}>
                  {formatVoiceLabel(v.name)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className='aichoice-actions'>
          <button className='yes' onClick={handleConfirm}>
            {confirmLabel}
          </button>
          <button className='no' onClick={handleCancel}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default React.memo(VoiceSelectionModal)
